import { Box, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AppointmentScheduler, {
  FetchDataType,
} from "../../components/RegistrationProgressComponents/FormScheduleAppointmentSelectDate";
import { APPOINTMENT_TITLE } from "../../config";
import { Slot } from "../../services/dto/Common";
import { User } from "../../services/dto/Security";
import UserService from "../../services/userService";
import { showApiError, showSuccess } from "../../services/utils";
import { useBypassToken } from "../hooks/useBypassToken";
import { Appointment } from "../types/AppointmentTypes";
import { Appointment as LegacyAppointment } from "../../services/dto/Common";
import PageTitle from "@components/PageTitle";

const DEFAULT_WINDOW_LENGTH = 7;

const MoveUpAppointmentPage: React.FC = () => {
  const [user, setUser] = useState<User>();
  const [timeZone, setTimeZone] = useState<string>();
  const [windowLength, setWindowLength] = useState<number>(DEFAULT_WINDOW_LENGTH);
  const [appointment, setAppointment] = useState<Appointment>();
  const [provider, setProvider] = useState<User>();
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<Slot>();
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [finished, setFinished] = useState(false);
  const [availData, setAvailData] = useState<any>();
  const [providerHasSlots, setProviderHasSlots] = useState<boolean>();

  const history = useHistory();
  const location = useLocation();
  const tokenData = useBypassToken();
  const searchParams = new URLSearchParams(location.search);
  const appointmentId = searchParams.get("appointmentId") || tokenData.appointmentId;

  useEffect(() => {
		if (!appointmentId) return;

    const fetchInitialData = async () => {

      try {
        // Fetch appointment details
        const appointmentResponse = await UserService.getAppointment(appointmentId);
        const appointmentData = appointmentResponse.data;
        setAppointment(appointmentData);

        // Fetch user details
        const userResponse = await UserService.getSimpleUser(appointmentData.patientId);
        const userData = userResponse.data;
        setUser(userData);
        setTimeZone(userData.timeZone);

        // Fetch provider details
        const providerResponse = await UserService.getPhysician(appointmentData.providerId);
        const providerData = providerResponse.data;
        setProvider(providerData);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        history.push("/");
      }
    };

    fetchInitialData();
  }, [appointmentId, history]);

  useEffect(() => {
    if (user && provider && timeZone) {
      getPhysicianSchedule();
    }
  }, [user, provider, timeZone]);

	if (!appointmentId) {
		return (
			<Box sx={{ padding: 3 }}>
        <Typography color="error">Appointment ID not found. Cannot load form.</Typography>
      </Box>
		)
	}

  const getPhysicianSchedule = async (params: any = {}) => {
    if (!user || !provider || !timeZone || !windowLength) return;

    setLoading(true);
    const data = {
      providerId: provider.id,
      startTime: moment().add(1, "days").startOf("day").toISOString(),
      endTime: moment().add(1 + windowLength, "days").endOf("day").toISOString(),
      timeZone,
      leadTime: 1,
      state: user.state,
      isNewPatient: !appointment?.followUp || user.isNewPatient,
      type: "pseudo",
      withSchedule: true,
      ...params,
    };

    try {
      const response = await UserService.getSchedule(data, true);
      setAvailData(response.data);
      setProviderHasSlots(!_.isEmpty(response.data?.availableSlots));
    } catch (error) {
      console.error("Error fetching schedule:", error);
      history.push("/");
    } finally {
      setLoading(false);
    }
  };

  const onFetchData = (changes: FetchDataType) => {
    if (changes.timeZone) setTimeZone(changes.timeZone);
    getPhysicianSchedule(changes);
  };

  const onSaveAppointment = async (slot?: Slot) => {
    if (!provider || !slot || !user || !appointment) {
      showApiError(new Error("Invalid context"));
      return;
    }

    setSavingAppointment(true);
    const body = {
      description: APPOINTMENT_TITLE,
      providerId: provider.id,
      patientId: user.id,
      startTime: slot.start,
      endTime: slot.end,
      on: moment(slot.start).format("MMM DD, YYYY"),
      at: moment(slot.start).format("hh:mm a"),
    };

    try {
      await UserService.updateAppointment(appointment.id, body);
      showSuccess("Appointment has been updated successfully!");
      setFinished(true);
    } catch (error) {
      showApiError(error);
    } finally {
      setSavingAppointment(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <PageTitle title="Edit Appointment" />

      {finished && (
        <Box sx={{ mb: 3, p: 2, m: 1, bgcolor: "success.light", borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Appointment Rescheduled!
          </Typography>
          <Typography>You are all set. You can close this tab now.</Typography>
        </Box>
      )}

      {!loading && user && provider && timeZone && (
        <Box sx={{ mt: 3 }}>
          <AppointmentScheduler
            data={availData}
            provider={provider}
            existingApt={appointment as unknown as LegacyAppointment} // TODO: reconcile User and Appointment types between dto/Common and revamp/types
            fetchData={onFetchData}
            title={
              appointment?.followUp || !user.isNewPatient
                ? "Follow-Up Visit"
                : "New Patient Consultation"
            }
            timeZone={timeZone}
            duration={appointment?.followUp || !user.isNewPatient ? 20 : 40}
            onSelect={(s?: Slot) => setSelectedSlot(s)}
            onConfirm={(s: Slot) => onSaveAppointment(s)}
            submitting={savingAppointment}
            showProviderToggle={false}
            defaultDate={dayjs().add(1, "days")}
            hideTopCard={finished}
            hideButtons={finished}
          />
        </Box>
      )}

      {!loading && !selectedSlot && !finished && (
        <Typography sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}>
          To reschedule to a date beyond this range, you will need to{" "}
          <a href="/signInPage" style={{ textDecoration: "underline" }}>
            login
          </a>{" "}
          and edit your existing appointment.
        </Typography>
      )}
    </Box>
  );
};

export default MoveUpAppointmentPage;
